import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Grid, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import React, { FC, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AdminLayoutView from 'src/Layout/AdminLayout/AdminLayoutView';
import AdminTabs from 'src/Layout/AdminLayout/AdminTabs';
import { AdminTabValue } from 'src/Layout/AdminLayout/AdminTabs/types';
import AdminViewWrapper from 'src/Layout/AdminLayout/AdminViewWrapper';
import { CopyToClipboardButton } from 'src/components/CopyToClipboardButton';
import { getFullDate } from 'src/helpers/date';
import { useAdminSignupViewController } from '../../controllers';
import { AdminSignup } from '../../types';

export const AdminSignupView = () => {
  const { signupId, signup, isLoading, notFound } = useAdminSignupViewController();

  return (
    <AdminViewWrapper>
      <AdminLayoutView tabs={<AdminTabs value={AdminTabValue.signups} />}>
        {isLoading && <LinearProgress />}

        {notFound && (
          <Stack alignItems="flex-start" gap={2} p={3}>
            <Typography variant="h6">No signup with this id</Typography>
            <Typography variant="body2" color="text.secondary">
              {signupId}
            </Typography>
            <Button component={RouterLink} to="/admin/signups" variant="contained">
              Back to signups
            </Button>
          </Stack>
        )}

        {signup != null && <Details signup={signup} />}
      </AdminLayoutView>
    </AdminViewWrapper>
  );
};

const Details: FC<{ signup: AdminSignup }> = ({ signup }) => (
  <Grid container spacing={2} p={2}>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h6">{signup.email ?? 'no email yet'}</Typography>
        <Typography variant="body2" color="text.disabled">
          {signup.id}
        </Typography>
        <CopyToClipboardButton text={signup.id} variant="icon" />
        {/* The card is usually opened from a Slack link, so there is no history to go back through. */}
        <Link component={RouterLink} to="/admin/signups" ml="auto">
          All signups
        </Link>
      </Stack>
    </Grid>

    <Section title="Path">
      <Field label="Status" value={signup.status} />
      <Field label="Step" value={signup.step} />
      <Field label="Started" value={getFullDate(signup.dateCreated)} />
      <Field label="Email verified" value={dateOrDash(signup.dateEmailVerified)} />
      <Field label="Paid" value={dateOrDash(signup.datePaid)} />
      <Field label="Completed" value={dateOrDash(signup.dateCompleted)} />
      <Field label="Last activity" value={getFullDate(signup.dateLastActivity)} />
    </Section>

    <Section title="Purchase">
      <Field label="Plan" value={signup.planName ?? '—'} />
      <Field label="Number" value={signup.selectedNumber ?? '—'} />
      <Field label="Hold expires" value={dateOrDash(signup.reservationExpiresAt)} />
      <Field label="Card refused" value={dateOrDash(signup.datePaymentFailed)} />
    </Section>

    <Section title="Attribution">
      <Field label="Source" value={signup.source} />
      <Field label="Started from" value={signup.ip ?? '—'} />
      {Object.entries(signup.attribution).map(([key, value]) => (
        <Field key={key} label={key} value={value == null ? '—' : String(value)} />
      ))}
    </Section>

    <Section title="Account">
      <Field label="Verification attempts" value={String(signup.verificationAttempts)} />
      <Field
        label="Company"
        value={
          signup.companyId == null ? (
            'no company yet'
          ) : (
            <Link component={RouterLink} to={`/admin/companies/edit/${signup.companyId}`}>
              {signup.companyName ?? signup.companyId}
            </Link>
          )
        }
      />
    </Section>

    <Grid item xs={12}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Collected details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Shown as collected rather than laid out field by field: for a funnel that stopped before its
              company existed this is the only copy, and a layout would hide whatever it did not expect. */}
          <Typography component="pre" variant="body2" whiteSpace="pre-wrap" sx={{ overflowX: 'auto' }}>
            {JSON.stringify(signup.payload, null, 2)}
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Grid>
  </Grid>
);

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <Grid item xs={12} md={6}>
    <Card>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          {title}
        </Typography>
        <Stack gap={0.5}>{children}</Stack>
      </CardContent>
    </Card>
  </Grid>
);

const Field: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <Stack direction="row" gap={1} justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {value}
    </Typography>
  </Stack>
);

function dateOrDash(value: string | null): string {
  return value == null ? '—' : getFullDate(value);
}
